'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from '@/i18n/routing';
import { isPageNavigation } from './is-page-navigation';

// EWM-544 — a slim loading-progress bar pinned to the top of the viewport that
// tracks client-side page navigations. Pattern/behaviour mirrors the NProgress
// bar on the Jaycar retail sites; styling uses brand tokens here (exact retail
// values to be confirmed in QA).
//
// Start signals (a navigation begins):
//   - capture-phase link clicks to a different pathname (the common case;
//     fires at click time, before the RSC fetch, so the bar leads the load)
//   - back/forward (popstate)
//   - programmatic history.pushState/replaceState to a different pathname
//     (catches router.push to another page; same-pathname pushes — PLP
//     filters/sort/pagination — are intentionally ignored)
// Complete signal: the committed pathname changes (the destination rendered).
//
// It never starts on the initial load / hard refresh (no start signal fires)
// and never blocks interaction (pointer-events-none, aria-hidden).

const TRICKLE_START = 8; // % shown immediately so even instant navigations sweep
const TRICKLE_CEILING = 90; // trickle never reaches 100 until the route commits
const DONE_FADE_MS = 250;

const RouteProgress = () => {
  const pathname = usePathname();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  // URL the bar is currently tracking — guards against the pushState "echo"
  // restarting the same navigation, while still allowing a genuinely new
  // navigation to cancel-and-restart from zero.
  const trackingRef = useRef<string | null>(null);
  // The pathname the bar has last seen commit (the same signal that completes
  // it). Used to reject a start() for a destination we've already arrived at —
  // see the guard in start() and EWM-544's stuck-bar bug below.
  const committedPathRef = useRef<string | null>(null);
  const trickleTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopTrickle = useCallback(() => {
    if (trickleTimer.current) {
      clearInterval(trickleTimer.current);
      trickleTimer.current = null;
    }
  }, []);

  const start = useCallback(
    (nextHref: string) => {
      const targetUrl = new URL(nextHref, window.location.href);
      const target = targetUrl.href;
      // EWM-544 — never (re)start for a destination that has already committed.
      // router.push calls history.pushState from inside a useInsertionEffect
      // during the destination commit; our patch defers start() to a microtask,
      // which can flush AFTER the pathname-change effect already completed the bar
      // (done() nulls trackingRef). Without this guard that late echo restarts the
      // bar from ~8% on the already-rendered page and it sticks — never completing,
      // because there's no further pathname change to finish it (QA: "rerenders on
      // reaching the destination and gets stuck"). The committed pathname is the
      // exact signal done() keys off, so a start for it means "already here".
      if (committedPathRef.current === targetUrl.pathname) return;
      // Same navigation already tracked (e.g. the pushState echo after a click) — ignore.
      if (trackingRef.current === target) return;
      trackingRef.current = target;

      if (fadeTimer.current) clearTimeout(fadeTimer.current);
      stopTrickle();

      setVisible(true);
      setProgress(TRICKLE_START); // restarts from low for a new navigation (cancel + restart)
      trickleTimer.current = setInterval(() => {
        setProgress((p) => (p >= TRICKLE_CEILING ? p : p + Math.max(0.5, (TRICKLE_CEILING - p) * 0.08)));
      }, 200);
    },
    [stopTrickle],
  );

  const done = useCallback(() => {
    if (!trackingRef.current) return; // nothing in flight (e.g. initial load) — no-op
    trackingRef.current = null;
    stopTrickle();
    setProgress(100);
    fadeTimer.current = setTimeout(() => {
      setVisible(false);
      setProgress(0);
    }, DONE_FADE_MS);
  }, [stopTrickle]);

  // Complete the bar once the destination pathname has committed. Skips the
  // initial mount so a fresh page load / hard refresh never shows the bar.
  const mounted = useRef(false);
  useEffect(() => {
    // Record the committed pathname first — start()'s guard reads it to reject a
    // late echo for a route we've already arrived at (EWM-544). Uses
    // window.location so it matches the resolved-target pathname start() compares.
    committedPathRef.current = window.location.pathname;
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    done();
  }, [pathname, done]);

  useEffect(() => {
    const here = () => window.location.href;

    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as HTMLElement | null)?.closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      if (isPageNavigation(here(), href)) start(href);
    };

    const onPopState = () => {
      // back/forward lands on a (usually) different pathname; the pathname
      // effect completes it. Track by the post-navigation URL.
      start(here());
    };

    const origPush = window.history.pushState.bind(window.history);
    const origReplace = window.history.replaceState.bind(window.history);
    const patched =
      (orig: typeof window.history.pushState) =>
      (...args: Parameters<typeof window.history.pushState>) => {
        const url = args[2];
        // Decide + resolve the target BEFORE the navigation commits — once orig()
        // runs, window.location is already the destination, so isPageNavigation()
        // / URL resolution would compare the destination against itself.
        const shouldStart = url != null && isPageNavigation(here(), String(url));
        const target = shouldStart ? new URL(String(url), here()).href : null;
        const result = orig(...args);
        // Next.js 16 / React 19 invoke history.pushState from inside a
        // useInsertionEffect during router transitions (router.push). Scheduling
        // setState synchronously there throws "useInsertionEffect must not
        // schedule updates", so defer start() to a microtask — it runs right
        // after the commit's synchronous work, so the bar still leads the load.
        // (Click/popstate paths are real DOM events and start() directly.) The
        // pushState echo after a click is still deduped inside start().
        if (target) queueMicrotask(() => start(target));
        return result;
      };
    window.history.pushState = patched(origPush);
    window.history.replaceState = patched(origReplace);

    document.addEventListener('click', onClick, { capture: true });
    window.addEventListener('popstate', onPopState);

    return () => {
      document.removeEventListener('click', onClick, { capture: true });
      window.removeEventListener('popstate', onPopState);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
      stopTrickle();
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
    };
  }, [start, stopTrickle]);

  return (
    // EWM-544 — the bar renders as a WHITE fill. It sits above the navy sticky
    // header; QA/design (Luke) found a navy bar on the navy header hard to parse,
    // so white gives clear contrast. White is a plain colour (not a theme token),
    // so — unlike the earlier `bg-primary` version — the bar needs no
    // `[data-theme]` scope to resolve its fill. It's still mounted high in the
    // tree (inside I18nProvider per EWM-587) but outside the themed wrapper; that
    // no longer matters now the fill doesn't depend on `--color-primary`.
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-[9999] h-[3px]"
      style={{ opacity: visible ? 1 : 0, transition: `opacity ${DONE_FADE_MS}ms ease` }}
    >
      <div
        className="h-full bg-white shadow-[0_0_8px_rgba(0,0,0,0.35)]"
        style={{ width: `${progress}%`, transition: 'width 200ms ease' }}
      />
    </div>
  );
};

export default RouteProgress;
